import { Minus, Plus } from '@phosphor-icons/react';
import Tippy from '@tippyjs/react';
import { ChangeEvent, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { MediaDisplay } from '@manifoldxyz/react-component-library';
import StyledButton from '@/components/StyledButton';
import { useClaim } from '@/hooks/mainStore';
import useMainStore from '@/store/mainStore';

interface CheckoutTokenProps {
  image: string;
  name: string;
  count?: string; // Optional since we get the value from the store
  disableQuantitySelector?: boolean; // New prop to disable the quantity selector
}

export default function CheckoutToken({
  image,
  name,
  disableQuantitySelector = false,
}: CheckoutTokenProps) {
  const { claim: claimOnChain } = useClaim();
  const numberOfMints = useMainStore((state) => state.numberOfMints);
  const setNumberOfMints = useMainStore((state) => state.setNumberOfMints);
  const [inputValue, setInputValue] = useState<string>(numberOfMints.toString());

  // Update input value when numberOfMints changes externally
  useEffect(() => {
    setInputValue(numberOfMints.toString());
  }, [numberOfMints]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Allow empty string for better UX while typing
    setInputValue(e.target.value);

    // Only update store if it's a valid number
    const newValue = parseInt(e.target.value);
    if (!isNaN(newValue) && newValue > 0) {
      setNumberOfMints(newValue);
    }
  };

  const handleBlur = () => {
    // Reset to 1 if input is empty or invalid when user leaves the field
    if (!inputValue || isNaN(parseInt(inputValue)) || parseInt(inputValue) < 1) {
      setInputValue('1');
      setNumberOfMints(1);
    }
  };

  const incrementMints = () => {
    const newValue = numberOfMints + 1;
    setNumberOfMints(newValue);
    setInputValue(newValue.toString());
  };

  const decrementMints = () => {
    if (numberOfMints > 1) {
      const newValue = numberOfMints - 1;
      setNumberOfMints(newValue);
      setInputValue(newValue.toString());
    }
  };

  const maxSupplyReached =
    (claimOnChain?.walletMax && numberOfMints >= claimOnChain?.walletMax) ||
    (claimOnChain?.totalMax && claimOnChain?.total + numberOfMints >= claimOnChain?.totalMax);

  const studioInstance = useMainStore((state) => state.studioInstance);
  const description = studioInstance?.publicData.asset.description;
  return (
    <div className="m-claim--flex m-claim--items-center m-claim--gap-4">
      {/* Token Image */}
      <div className="m-claim--aspect-square m-claim--w-12 m-claim--overflow-hidden">
        {image ? (
          <MediaDisplay
            imageUrl={image}
            showImageOnly={true}
            className="m-claim--size-full m-claim--object-cover"
          />
        ) : (
          <Skeleton
            height="100%"
            width="100%"
          />
        )}
      </div>

      {/* Token Details */}
      <div className="m-claim--flex m-claim--flex-col m-claim--gap-1">
        <span className="m-claim--text-sm m-claim--font-medium">{name}</span>
        <span className="m-claim--text-xs m-claim--text-secondary">
          {StringUtils.abbreviate(description || '', 10, 0)}
        </span>
      </div>

      {/* Number Selector - Hidden when disableQuantitySelector is true */}
      {!disableQuantitySelector ? (
        <div className="m-claim--ml-auto m-claim--flex m-claim--items-center m-claim--gap-1.5">
          <StyledButton
            version="secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '1/1',
              padding: '0',
              width: '63px',
              height: '42px',
            }}
            onClick={decrementMints}
            disabled={numberOfMints <= 1}
            aria-label="Decrease number of mints"
            tabIndex={0}
          >
            <Minus
              size={20}
              weight="bold"
            />
          </StyledButton>

          <div className="m-claim--relative m-claim--h-full m-claim--w-24">
            <input
              type="text"
              inputMode="numeric"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className="m-claim--size-full m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--p-2.5 m-claim--text-center m-claim--text-sm m-claim--font-medium m-claim--text-primary m-claim--outline-none focus:m-claim--outline-none focus-visible:m-claim--outline-none"
              style={{ boxShadow: 'none' }}
              aria-label="Number of mints"
              tabIndex={0}
            />
          </div>

          {maxSupplyReached ? (
            <Tippy
              content="Limited supply reached"
              maxWidth={200}
            >
              <span
                className="m-claim--cursor-not-allowed"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  aspectRatio: '1/1',
                  padding: '0',
                  width: '63px',
                  height: '42px',
                }}
              >
                <StyledButton
                  onClick={incrementMints}
                  version="secondary"
                  aria-label="Increase number of mints"
                  tabIndex={0}
                  disabled={true}
                >
                  <Plus
                    size={20}
                    weight="bold"
                  />
                </StyledButton>
              </span>
            </Tippy>
          ) : (
            <StyledButton
              onClick={incrementMints}
              version="secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                aspectRatio: '1/1',
                padding: '0',
                width: '63px',
                height: '42px',
              }}
              aria-label="Increase number of mints"
              tabIndex={0}
              disabled={false}
            >
              <Plus
                size={20}
                weight="bold"
              />
            </StyledButton>
          )}
        </div>
      ) : (
        <div className="m-claim--ml-auto m-claim--flex m-claim--items-center m-claim--rounded-md m-claim--border m-claim--border-default m-claim--bg-surface-primary m-claim--px-4 m-claim--py-2 m-claim--text-center m-claim--text-sm m-claim--font-medium m-claim--text-primary">
          1
        </div>
      )}
    </div>
  );
}
